import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaCheckCircle, FaTimesCircle, FaFileInvoice, FaFileInvoiceDollar } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import * as XLSX from 'xlsx';
import Contenedor from '../GlobalComponents/Contenedor';

const tiposPago = [
  { value: '', label: 'Todos' },
  { value: 'Curso', label: 'Curso' },
  { value: 'Marketplace', label: 'Marketplace' },
  { value: 'Membresía', label: 'Membresía' },
  { value: 'Membresía Paciente Premium', label: 'Membresía Paciente Premium' },
  { value: 'Upgrade a Premium', label: 'Upgrade a Premium (histórico)' },
  { value: 'Cita', label: 'Cita' },
];

const estadosPago = [
  { value: '', label: 'Todos' },
  { value: 'Aprobado', label: 'Aprobado' },
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'Rechazado', label: 'Rechazado' },
];

const Facturacion = () => {
  const [pagos, setPagos] = useState([]);
  const [pagosFiltrados, setPagosFiltrados] = useState([]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [tipoPago, setTipoPago] = useState('');
  const [estadoPago, setEstadoPago] = useState('');
  const [detallePago, setDetallePago] = useState(null);

  useEffect(() => {
    fetchPagos();
  }, []);

  useEffect(() => {
    filtrarPagos();
    // eslint-disable-next-line
  }, [pagos, fechaInicio, fechaFin, tipoPago, estadoPago]);

  const fetchPagos = async () => {
    try {
      const res = await apiClient.get('/api/facturacion/pagos');
      setPagos(res.data);
    } catch {
      setPagos([]);
    }
  };

  const toDateOnly = (dateStr) => {
    const d = new Date(dateStr);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const filtrarPagos = () => {
    let filtrados = [...pagos];
    if (fechaInicio) {
      const inicio = toDateOnly(fechaInicio);
      filtrados = filtrados.filter(p => toDateOnly(p.fecha) >= inicio);
    }
    if (fechaFin) {
      const fin = toDateOnly(fechaFin);
      filtrados = filtrados.filter(p => toDateOnly(p.fecha) <= fin);
    }
    if (tipoPago) {
      filtrados = filtrados.filter(p => p.concepto === tipoPago);
    }
    if (estadoPago) {
      filtrados = filtrados.filter(p => p.estado === estadoPago);
    }
    
    // Ordenar por fecha descendente (más reciente primero)
    // Usar fechaOriginal si existe (formato ISO), sino usar la fecha formateada
    filtrados.sort((a, b) => {
      let fechaA, fechaB;
      
      if (a.fechaOriginal) {
        fechaA = new Date(a.fechaOriginal);
      } else {
        // Parsear fecha formateada: "YYYY-MM-DD hh:mm:ss AM/PM"
        const fechaStrA = a.fecha.replace(' AM', '').replace(' PM', '');
        const [datePartA, timePartA] = fechaStrA.split(' ');
        const [yearA, monthA, dayA] = datePartA.split('-');
        const [hourA, minA, secA] = timePartA.split(':');
        let hour24A = parseInt(hourA);
        if (a.fecha.includes('PM') && hour24A !== 12) hour24A += 12;
        if (a.fecha.includes('AM') && hour24A === 12) hour24A = 0;
        fechaA = new Date(yearA, monthA - 1, dayA, hour24A, minA, secA);
      }
      
      if (b.fechaOriginal) {
        fechaB = new Date(b.fechaOriginal);
      } else {
        // Parsear fecha formateada: "YYYY-MM-DD hh:mm:ss AM/PM"
        const fechaStrB = b.fecha.replace(' AM', '').replace(' PM', '');
        const [datePartB, timePartB] = fechaStrB.split(' ');
        const [yearB, monthB, dayB] = datePartB.split('-');
        const [hourB, minB, secB] = timePartB.split(':');
        let hour24B = parseInt(hourB);
        if (b.fecha.includes('PM') && hour24B !== 12) hour24B += 12;
        if (b.fecha.includes('AM') && hour24B === 12) hour24B = 0;
        fechaB = new Date(yearB, monthB - 1, dayB, hour24B, minB, secB);
      }
      
      return fechaB - fechaA; // Orden descendente (más reciente primero)
    });
    
    setPagosFiltrados(filtrados);
  };

  const exportarExcel = () => {
    if (!pagosFiltrados.length) {
      alert('No hay datos para exportar');
      return;
    }
    let data;
    // Si el filtro es solo Cita, agrega los campos extra
    if (tipoPago === 'Cita') {
      data = pagosFiltrados.map(pago => ({
        Fecha: pago.fecha,
        Usuario: pago.usuario,
        Concepto: pago.concepto,
        Monto: pago.monto,
        Estado: pago.estado,
        Metodo: pago.metodo,
        Referencia: pago.referencia,
        'Monto Doctor': pago.montoDoctor || pago.monto_doctor || 0,
        'Monto Redplika': pago.montoSalutio || pago.monto_salutio || 0,
        'Facturado Siigo': pago.siigoFacturaId ? 'Sí' : 'No',
        'ID Factura Siigo': pago.siigoFacturaId || 'N/A',
        'URL PDF': pago.siigoFacturaPdfUrl || 'N/A',
        'URL XML': pago.siigoFacturaXmlUrl || 'N/A'
      }));
    } else {
      data = pagosFiltrados.map(pago => ({
        Fecha: pago.fecha,
        Usuario: pago.usuario,
        Concepto: pago.concepto,
        Monto: pago.monto,
        Estado: pago.estado,
        Metodo: pago.metodo,
        Referencia: pago.referencia,
        'Facturado Siigo': pago.siigoFacturaId ? 'Sí' : 'No',
        'ID Factura Siigo': pago.siigoFacturaId || 'N/A',
        'URL PDF': pago.siigoFacturaPdfUrl || 'N/A',
        'URL XML': pago.siigoFacturaXmlUrl || 'N/A'
      }));
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pagos');
    // Nombre del archivo según filtros
    let nombre = 'pagos';
    if (fechaInicio && fechaFin) nombre += `_del_${fechaInicio}_al_${fechaFin}`;
    if (tipoPago) nombre += `_tipo_${tipoPago}`;
    if (estadoPago) nombre += `_estado_${estadoPago}`;
    nombre += '.xlsx';
    XLSX.writeFile(wb, nombre);
  };

  const exportarExcelCitas = () => {
    const soloCitas = pagosFiltrados.filter(p => p.concepto === 'Cita');
    if (!soloCitas.length) {
      alert('No hay pagos de cita para exportar');
      return;
    }
    const data = soloCitas.map(pago => ({
      Fecha: pago.fecha,
      Usuario: pago.usuario,
      Concepto: pago.concepto,
      Monto: pago.monto,
      Estado: pago.estado,
      Metodo: pago.metodo,
      Referencia: pago.referencia,
      'Monto Doctor': pago.montoDoctor || pago.monto_doctor || 0,
      'Monto Redplika': pago.montoSalutio || pago.monto_salutio || 0
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Citas');
    let nombre = 'pagos_citas';
    if (fechaInicio && fechaFin) nombre += `_del_${fechaInicio}_al_${fechaFin}`;
    if (estadoPago) nombre += `_estado_${estadoPago}`;
    nombre += '.xlsx';
    XLSX.writeFile(wb, nombre);
  };

  return (
    <Contenedor title="Facturación" icon={FaFileInvoiceDollar}>
      {/* Modal de detalle */}
      {detallePago && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-200">
            <div className="sticky top-0 bg-gradient-to-r from-[#1f94c2] to-[#205087] px-5 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-semibold text-white">Detalle del pago</h3>
              <button
                className="w-8 h-8 rounded-lg bg-white/20 text-white hover:bg-white/30 flex items-center justify-center text-xl font-bold"
                onClick={() => setDetallePago(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              {/* Indicador de facturación Siigo */}
              {detallePago.siigoFacturaId && (detallePago.siigoFacturaPdfUrl || detallePago.siigoFacturaXmlUrl) ? (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <FaCheckCircle className="text-green-600 shrink-0" />
                    <span className="font-semibold">Facturado electrónicamente (Siigo)</span>
                  </div>
                  <p className="text-sm text-gray-600 break-all"><strong>ID:</strong> {detallePago.siigoFacturaId}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {detallePago.siigoFacturaPdfUrl && (
                      <a href={detallePago.siigoFacturaPdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1f94c2] hover:text-[#205087] font-medium inline-flex items-center gap-1">
                        <FaFileInvoice /> PDF
                      </a>
                    )}
                    {detallePago.siigoFacturaXmlUrl && (
                      <a href={detallePago.siigoFacturaXmlUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1f94c2] hover:text-[#205087] font-medium inline-flex items-center gap-1">
                        <FaFileInvoice /> XML
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <FaTimesCircle className="text-red-600 shrink-0" />
                    <span className="font-semibold">Sin factura electrónica</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Este pago no tiene factura generada en Siigo.</p>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {Object.entries(detallePago).map(([key, value]) => (
                  (key !== 'montoDoctor' && key !== 'montoSalutio' && key !== 'splitConfigEnviado' && key !== 'epaycoValidacion' && key !== 'siigoFacturaId' && key !== 'siigoFacturaPdfUrl' && key !== 'siigoFacturaXmlUrl') && (
                    <div key={key} className="flex justify-between gap-2 border-b border-slate-100 pb-2">
                      <span className="font-medium text-gray-600 capitalize shrink-0">{key}:</span>
                      <span className="text-gray-800 text-right break-all">{String(value)}</span>
                    </div>
                  )
                ))}
              </div>
              {detallePago.concepto === 'Cita' && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
                  <div className="font-semibold text-[#205087] mb-1">Distribución esperada (70% / 30%)</div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total pagado</span>
                    <span className="font-semibold">${Number(detallePago.monto || 0).toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Doctor (70%)</span>
                    <span className="font-semibold text-green-700">${Number(detallePago.montoDoctor || 0).toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Redplika (30%)</span>
                    <span className="font-semibold text-[#1f94c2]">${Number(detallePago.montoSalutio || 0).toLocaleString('es-CO')}</span>
                  </div>
                  {detallePago.nombreDoctor && (
                    <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
                      <span className="text-gray-600">Profesional</span>
                      <span className="text-gray-800 text-right">{detallePago.nombreDoctor}</span>
                    </div>
                  )}
                  {detallePago.epaycoRef && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Ref. ePayco</span>
                      <span className="text-gray-800 font-mono text-xs">{detallePago.epaycoRef}</span>
                    </div>
                  )}
                  {detallePago.splitConfigEnviado?.receiverId && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">ID receptor doctor</span>
                      <span className="text-gray-800 font-mono text-xs break-all text-right">{detallePago.splitConfigEnviado.receiverId}</span>
                    </div>
                  )}
                  {detallePago.epaycoValidacion?.x_amount != null && (
                    <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
                      <span className="text-gray-600">Confirmado ePayco</span>
                      <span className="text-gray-800">${Number(detallePago.epaycoValidacion.x_amount).toLocaleString('es-CO')} — {detallePago.epaycoValidacion.x_respuesta}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 pt-1">Los montos reales en cuentas ePayco pueden variar por comisiones del medio de pago (PSE, tarjeta, etc.).</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-6 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2]"
              value={fechaInicio}
              onChange={e => setFechaInicio(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2]"
              value={fechaFin}
              onChange={e => setFechaFin(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de pago</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2]"
              value={tipoPago}
              onChange={e => setTipoPago(e.target.value)}
            >
              {tiposPago.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2]"
              value={estadoPago}
              onChange={e => setEstadoPago(e.target.value)}
            >
              {estadosPago.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="flex items-center gap-2 bg-[#1f94c2] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#205087] transition-colors"
              onClick={exportarExcel}
            >
              <FaFileExcel /> Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Lista de pagos en tarjetas (sin scroll horizontal) */}
      <div className="space-y-3 max-w-full overflow-hidden">
        {pagosFiltrados.length === 0 ? (
          <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 text-gray-500 bg-slate-50/50">
            No hay pagos que coincidan con los filtros.
          </div>
        ) : (
          pagosFiltrados.map((pago, idx) => {
            const tieneFacturaSiigo = pago.siigoFacturaId && (pago.siigoFacturaPdfUrl || pago.siigoFacturaXmlUrl);
            return (
              <div
                key={idx}
                className={`rounded-xl border p-4 transition-shadow hover:shadow-md max-w-full overflow-hidden ${tieneFacturaSiigo ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-white'}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0 flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className="text-gray-500 block">Fecha</span>
                      <span className="font-medium text-gray-900 truncate block" title={pago.fecha}>{pago.fecha}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Usuario</span>
                      <span className="font-medium text-gray-900 truncate block" title={pago.usuario}>{pago.usuario}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Concepto</span>
                      <span className="font-medium text-gray-900">{pago.concepto}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Monto</span>
                      <span className="font-semibold text-[#205087]">${pago.monto}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Estado</span>
                      <span className="font-medium">{pago.estado}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Siigo</span>
                      {tieneFacturaSiigo ? (
                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                          <FaCheckCircle className="shrink-0" /> Facturado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                          <FaTimesCircle className="shrink-0" /> Sin facturar
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {pago.siigoFacturaPdfUrl && (
                      <a href={pago.siigoFacturaPdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#1f94c2] hover:text-[#205087] font-medium">
                        <FaFileInvoice /> PDF
                      </a>
                    )}
                    {pago.siigoFacturaXmlUrl && (
                      <a href={pago.siigoFacturaXmlUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#1f94c2] hover:text-[#205087] font-medium">
                        <FaFileInvoice /> XML
                      </a>
                    )}
                    <button
                      type="button"
                      className="text-sm font-medium text-[#1f94c2] hover:text-[#205087] underline"
                      onClick={() => setDetallePago(pago)}
                    >
                      Ver detalle
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Contenedor>
  );
};

export default Facturacion; 